/**
 * Pure helpers for the visual page editor (no Vue, unit-tested).
 */

/** string[] → textarea text, one value per line. Non-arrays render as empty. */
export function stringListToText(value: unknown): string {
  if (!Array.isArray(value)) return ''
  return value.filter((v) => typeof v === 'string').join('\n')
}

/** Textarea text → string[]: one value per line, trimmed, blank lines dropped. */
export function textToStringList(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
}

/**
 * Return a new array with the element at `from` moved to index `to`
 * (index in the resulting array). Out-of-range indexes return a copy unchanged.
 */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const next = [...list]
  if (from < 0 || from >= next.length || to < 0 || to >= next.length || from === to) return next
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item as T)
  return next
}

/** Empty value for a new array item's field, by editor field type. */
export function emptyValueForField(fieldDef: { type: string; options?: string[] }): unknown {
  switch (fieldDef.type) {
    case 'string':
    case 'textarea':
    case 'url':
      return ''
    case 'number':
      return 0
    case 'boolean':
      return false
    case 'select':
      return fieldDef.options?.[0] || ''
    case 'array':
    case 'stringList':
      return []
    default:
      return null
  }
}

/**
 * New sibling order after dropping `draggedId` before/after `targetId`.
 * Returns null when the drop is a no-op or either id isn't in `ids`.
 */
export function reorderIds(ids: readonly number[], draggedId: number, targetId: number, after: boolean): number[] | null {
  if (draggedId === targetId || !ids.includes(draggedId) || !ids.includes(targetId)) return null
  const next = ids.filter((id) => id !== draggedId)
  const at = next.indexOf(targetId) + (after ? 1 : 0)
  next.splice(at, 0, draggedId)
  return next.every((id, i) => id === ids[i]) ? null : next
}

interface ReorderableComponent {
  id: number
  position: number
  children?: ReorderableComponent[] | undefined
}

/**
 * Optimistically apply a sibling reorder to the page-components query data
 * (roots + one level of `children`). `parentInstanceId` null = root list.
 */
export function applyReorder<T extends ReorderableComponent>(
  list: readonly T[],
  parentInstanceId: number | null,
  orderedIds: readonly number[],
): T[] {
  const rank = new Map(orderedIds.map((id, i) => [id, i]))
  const reorder = <C extends ReorderableComponent>(items: readonly C[]): C[] =>
    items
      .map((c) => (rank.has(c.id) ? { ...c, position: rank.get(c.id)! } : c))
      .sort((a, b) => a.position - b.position)

  if (parentInstanceId === null) return reorder(list)
  return list.map((c) =>
    c.id === parentInstanceId && c.children ? { ...c, children: reorder(c.children) } : c,
  )
}
